import React, { useState, useCallback, useLayoutEffect, useRef, useMemo } from 'react';
import { Alert, View, StyleSheet, Keyboard } from 'react-native';
import { format } from 'date-fns';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { AppView, NavigationHeader, Spacer } from 'design-system';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SettingsSection } from 'design-system/SettingsSection';
import { IconBoldCalendar, IconProfile } from 'design-system/icons';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { SettingsItemInput, SettingsItemNavigate } from 'design-system/SettingsItem';
import useColors from 'hooks/useColors';
import { useDateTimePicker } from 'hooks';
import { validateName } from 'utils/validation';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { DOB_DISPLAY_FORMAT } from 'hooks/useDateTimePickerDOB';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppDispatch } from 'store/hooks';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { OnboardingFlow } from 'utils/types/navigation';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
const strings = {
    dob: 'Date of birth',
    buttonNext: 'Continue',
    alertTitle: 'Hold on 💫',
    defaultTitle: 'Choose...',
    lastName: 'Legal last name',
    firstName: 'Legal first name',
    lastNamePlaceholder: 'Child’s last name...',
    firstNamePlaceholder: 'Child’s first name...',
    alertInvalidLastName: 'Please enter a valid last name with at least 2 letters. Names can only contain letters A-Z (accents are fine), spaces, hyphens and apostrophes.',
    alertInvalidFirstName: 'Please enter a valid first name with at least 2 letters. Names can only contain letters A-Z (accents are fine), spaces, hyphens and apostrophes.',
};
const JISAChildPersonalDetails = ({ route, navigation }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const scrollY = useSharedValue(0);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const navigateToNextScreen = useNavigateToNextScreen();
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const [lastName, setLastName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [dob, setDob] = useState();
    const [isDOBOpen, setIsDOBOpen] = useState(false);
    const [isChildFromUK, setIsChildFromUK] = useState(false);
    const { showDateTimePicker } = useDateTimePicker({
        initialValue: dob ? new Date(dob) : new Date(),
        maximumDate: new Date(),
        onSubmitDate: (date) => {
            setDob(date);
        },
        onCloseModal: () => setIsDOBOpen(false),
    });
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useAutoFocusInput(firstNameRef);
    const onSubmit = useCallback(() => {
        const finalFirstName = firstName.trim();
        const finalLastName = lastName.trim();
        if (!validateName(finalFirstName)) {
            Alert.alert(strings.alertTitle, strings.alertInvalidFirstName);
            return;
        }
        if (!validateName(finalLastName)) {
            Alert.alert(strings.alertTitle, strings.alertInvalidLastName);
            return;
        }
        if (finalFirstName && finalLastName && dob && isChildFromUK) {
            dispatch({ type: 'SAVE_ONBOARDING_STATE', firstName: finalFirstName, lastName: finalLastName, dob });
            navigateToNextScreen();
        }
    }, [firstName, lastName, dob, isChildFromUK, navigateToNextScreen]);
    const handleConfirmPress = useCallback(() => {
        setIsChildFromUK((oldState) => !oldState);
    }, []);
    const handleDOBPress = useCallback(() => {
        Keyboard.dismiss();
        setIsDOBOpen(true);
        showDateTimePicker();
    }, [showDateTimePicker]);
    const openDobPickerIfNecessary = useCallback(() => {
        if (!dob) {
            handleDOBPress();
        }
    }, [dob, handleDOBPress]);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    return (<AppView withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightBtnContainer}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyles}>
        <ScreenInfoHeader title="Add your child’s personal details" subTitle="Get started by adding some basic information about your child"/>
        <SettingsSection noMarginTop>
          <SettingsItemInput ref={firstNameRef} value={firstName} submitBehavior="submit" onChangeText={setFirstName} returnKeyType="next" description={strings.firstName} placeholder={strings.firstNamePlaceholder} icon={<IconProfile gradientKey="skyBlue"/>} onSubmitEditing={() => lastNameRef.current?.focus()}/>
          <SettingsItemInput ref={lastNameRef} value={lastName} returnKeyType="next" onChangeText={setLastName} description={strings.lastName} placeholder={strings.lastNamePlaceholder} icon={<IconProfile gradientKey="yellow"/>} onSubmitEditing={openDobPickerIfNecessary}/>
          <SettingsItemNavigate isSelected={isDOBOpen} onPress={handleDOBPress} description={strings.dob} icon={<IconBoldCalendar gradientKey="peach"/>} title={dob ? format(dob, DOB_DISPLAY_FORMAT) : strings.defaultTitle}/>
        </SettingsSection>
        <TouchableBounceCard onDark onPress={handleConfirmPress}>
          <View style={styles.row}>
            <RadioButton type="multiple" isChecked={isChildFromUK}/>
            <Spacer w={16}/>
            <View style={styles.flex}>
              <Text TextThin-14>I confirm that my child is a UK resident and is not a US person.</Text>
            </View>
          </View>
        </TouchableBounceCard>
      </Animated.ScrollView>
      <Button brand floating onPress={onSubmit} title={strings.buttonNext} isInTab={!!mountedOnAnotherScreen} disabled={!firstName.trim() || !lastName.trim() || !dob || !isChildFromUK}/>
    </AppView>);
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
    },
    flex: {
        flex: 1,
    },
    rightBtnContainer: {
        marginBottom: rem(6),
        marginRight: rem(16),
        alignItems: 'flex-end',
    },
});
export default JISAChildPersonalDetails;
